class FormSubscription {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('.form-subscription__form');
    this.variantInput = container.querySelector('input[name="id"]');

    const variantsScript = container.querySelector('.form-subscription__variants-data');
    const optionsScript = container.querySelector('.form-subscription__options-data');

    this.variants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
    this.options = optionsScript ? JSON.parse(optionsScript.textContent || '{}') : {};
    this.applyFreeTravelMugTest();
    this.useSectionFirstOrderDiscount = container.dataset.useSectionFirstOrderDiscount === 'true';
    this.firstOrderDiscountPercent = this.useSectionFirstOrderDiscount ? this.parseDecimal(container.dataset.firstOrderDiscountPercent, 0) : 0;
    this.firstOrderDiscountAmount = this.useSectionFirstOrderDiscount ? this.parseDecimal(container.dataset.firstOrderDiscountAmount, 0) * 100 : 0;
    this.directCheckout = container.dataset.directCheckout === 'true';
    this.cumulateGifts = container.dataset.cumulateGifts === 'true';
    // Welcome offers hand out the gift bundle on one-time purchases too. The gift lines
    // stay free thanks to a SupaEasy rule that also accepts lines without a selling plan.
    this.giftsOneTimePurchase = container.dataset.giftsOneTimePurchase === 'true';

    this.enableAutomaticDiscount = container.dataset.enableAutomaticDiscount === 'true';
    this.automaticDiscountPercent = this.parseDecimal(container.dataset.automaticDiscountPercent, 0);
    this.automaticDiscountAmount = this.parseDecimal(container.dataset.automaticDiscountAmount, 0) * 100;
    this.automaticDiscountSubscriptionOnly = container.dataset.automaticDiscountSubscriptionOnly !== 'false';
    this.automaticDiscountPercentOneTime = this.parseDecimal(
      container.dataset.automaticDiscountPercentOneTime,
      this.automaticDiscountPercent
    );

    this.hasSubvariants = container.dataset.hasSubvariants !== 'false';
    this.usePickerFlavorDimension = container.dataset.usePickerFlavorDimension === 'true';

    // theme.default_subscription metafield state: 'true' | 'false' | 'undefined'.
    // Only the 'undefined' case is governed by the subscription-by-default A/B test.
    this.defaultSubscriptionState = container.dataset.defaultSubscription;

    // When enableAutomaticDiscount is true, subscription (and optionally one-shot) prices use automatic discount instead of section first-order discount.
    this.state = {
      selectedFlavor: container.dataset.currentFlavor || this.options.flavors?.[0] || '',
      selectedQuantityIndex: this.getInitialQuantityIndex(),
      subscriptionActive: this.resolveInitialSubscriptionActive(container.dataset.subscriptionActive === 'true')
    };
    // Keep the container attribute in sync so the CSS (toggle + switcher visibility)
    // matches the resolved state even when the test overrides the server default.
    this.container.dataset.subscriptionActive = this.state.subscriptionActive.toString();

    this.init();
  }

  // V_PT_PDP | "Var A - Subscription by default" Intelligems test
  // (4da94897-39d1-485a-97a6-dc42c3434972). When theme.default_subscription is undefined,
  // the control (and anyone not in the test) keeps one-time purchase and Var A defaults to
  // subscription. Explicit 'true'/'false' metafield values are decided server-side and are
  // never overridden here. Intelligems is render-blocking so igData is normally ready by the
  // time this runs; applySubscriptionByDefaultTest() re-checks on "ig:ready" as a safety net.
  resolveInitialSubscriptionActive(serverValue) {
    if (this.defaultSubscriptionState !== 'undefined') return serverValue;
    const testGroup = window.igData?.user?.getTestGroup(
      '4da94897-39d1-485a-97a6-dc42c3434972'
    );
    return testGroup?.name === 'Var A - Subscription by default';
  }

  // RA_PT_PDP_4 | Free Travel Mug Intelligems test
  // (57123a32-40cf-43cc-b86d-794b827d453e). Var A keeps the travel mug in the welcome gift
  // set, the control gets the set without it. Swapping `gifts` here makes every downstream
  // consumer follow: the gift grid and preview, the gifts total, the [gifts_value] copy and
  // the lines sent to /cart/add.js. Variants whose `gifts_abtest` is empty keep the control
  // set, which is what the Liquid fallback already guarantees.
  applyFreeTravelMugTest() {
    if (this.freeTravelMugApplied) return;
    if (!window.cFreeTravelMugVarA?.()) return;
    this.freeTravelMugApplied = true;
    this.variants.forEach((variant) => {
      if (variant.gifts_abtest?.length) variant.gifts = variant.gifts_abtest;
    });
  }

  applySubscriptionByDefaultTest() {
    if (this.defaultSubscriptionState !== 'undefined') return;
    if (this.userToggledSubscription) return;
    const wantSubscription =
      window.igData?.user?.getTestGroup('4da94897-39d1-485a-97a6-dc42c3434972')?.name ===
      'Var A - Subscription by default';
    if (wantSubscription !== this.state.subscriptionActive) {
      this.toggleSubscription();
    }
  }

  getInitialQuantityIndex() {
    const active = this.container.querySelector('.form-subscription__variant--active');
    if (active) return parseInt(active.dataset.quantityIndex || '0', 10);
    return 0;
  }

  /**
   * Parse a numeric string (e.g. from data attributes or Liquid). Accepts decimals with dot or comma.
   */
  parseDecimal(value, defaultValue = 0) {
    if (value === undefined || value === null || value === '') return defaultValue;
    const normalized = String(value).trim().replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : defaultValue;
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.updateUI();
    this.applyDefaultVariant();

    // Re-evaluate the subscription-by-default test once Intelligems signals readiness,
    // in case igData was not available yet when the initial state was resolved.
    if (this.defaultSubscriptionState === 'undefined') {
      window.addEventListener('ig:ready', () => this.applySubscriptionByDefaultTest());
    }

    window.addEventListener('ig:ready', () => {
      if (this.freeTravelMugApplied) return;
      this.applyFreeTravelMugTest();
      if (this.freeTravelMugApplied) this.updateGiftsUI();
    });
  }

  applyDefaultVariant() {
    const defaultVariantId = parseInt(this.container.dataset.defaultVariantId);
    if (!defaultVariantId) return;

    const defaultVariant = this.variants.find(v => v.id === defaultVariantId);
    if (!defaultVariant || !defaultVariant.available) return;

    const quantities = this.options.quantities || [];
    const optionValue = this.hasSubvariants ? defaultVariant.option2 : defaultVariant.option1;
    const qtyIndex = quantities.indexOf(optionValue);

    if (this.hasSubvariants && defaultVariant.option1 && defaultVariant.option1 !== this.state.selectedFlavor) {
      this.state.selectedFlavor = defaultVariant.option1;
    }
    if (this.usePickerFlavorDimension && defaultVariant.picker_flavor && defaultVariant.picker_flavor !== this.state.selectedFlavor) {
      this.state.selectedFlavor = defaultVariant.picker_flavor;
    }
    if (qtyIndex !== -1 && qtyIndex !== this.state.selectedQuantityIndex) {
      this.state.selectedQuantityIndex = qtyIndex;
    }

    this.updateUI();
  }

  cacheElements() {
    this.personButtons = this.container.querySelectorAll('.form-subscription__person');
    this.flavorButtons = this.container.querySelectorAll('.form-subscription__flavor-btn:not(.multiref-picker *)');
    this.variantItems = this.container.querySelectorAll('.form-subscription__variant');
    this.toggle = this.container.querySelector('.form-subscription__toggle');
    this.switcher = this.container.querySelector('.form-subscription__switcher');
    this.switcherSubOption = this.switcher?.querySelector('[data-switcher="subscription"]');
    this.switcherOneTimeOption = this.switcher?.querySelector('[data-switcher="onetime"]');
    this.switcherDiscount = this.switcher?.querySelector('.form-subscription__switcher-discount');
    this.switcherBenefits = this.switcherSubOption?.querySelector('.form-subscription__switcher-benefits');
    this.switcherOneTimeBenefits = this.switcherOneTimeOption?.querySelector('.form-subscription__switcher-benefits');
    this.discountBanner = this.container.querySelector(
      '.form-subscription__discount-banner:not(.form-subscription__discount-banner--stock)'
    );
    this.ctaPrice = this.container.querySelector('.form-subscription__cta-price');
    this.ctaCompare = this.container.querySelector('.form-subscription__cta-compare');
    this.ctaButton = this.container.querySelector('.form-subscription__cta');
    this.ctaDot = this.container.querySelector('.form-subscription__cta-dot');
    this.ctaLabelAvailable = this.container.querySelector('.form-subscription__cta-label--available');
    this.ctaLabelUnavailable = this.container.querySelector('.form-subscription__cta-label--unavailable');

    this.stickyBar = document.querySelector('.navigation-vrac');
    this.stickyButton = this.stickyBar?.querySelector('.product-info__add-button button');
    this.stickyPriceEl = this.stickyBar?.querySelector('.product-form__price .price__current');
    this.stickyCompareEl = this.stickyBar?.querySelector('.product-form__price .price__was');

    const wrapper = this.container.closest('.form-subscription-wrapper');
    this.titleSection = wrapper?.querySelector('.product-form__title');
    this.titleTextWrap = this.titleSection?.querySelector('.product-form__title-text');
    this.titleHeading = this.titleTextWrap?.querySelector('.product-title');
    this.defaultProductTitle = this.titleTextWrap?.dataset.defaultProductTitle || this.titleHeading?.textContent?.trim() || '';
    this.titlePriceEls = this.titleSection?.querySelectorAll('.price__current') || [];
    this.titleCompareEls = this.titleSection?.querySelectorAll('.price__was') || [];
    this.titleDividedEls = this.titleSection?.querySelectorAll('.igPrice') || [];
    this.dividedBy = parseInt(this.container.dataset.dividedPrice || '3', 10);
  }

  bindEvents() {
    this.personButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const person = parseInt(btn.dataset.person || '1', 10);
        this.selectPerson(person);
      });
    });

    this.flavorButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const flavor = btn.dataset.flavor;
        if (!flavor) return;
        // Allow selecting out-of-stock flavors so the user sees the sold-out CTA state.
        this.selectFlavor(flavor);
      });
    });

    this.variantItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectQuantity(index);
      });
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.userToggledSubscription = true;
        this.toggleSubscription();
      });
    }

    if (this.switcherSubOption) {
      this.switcherSubOption.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.userToggledSubscription = true;
        if (!this.state.subscriptionActive) this.toggleSubscription();
      });
    }

    if (this.switcherOneTimeOption) {
      this.switcherOneTimeOption.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.userToggledSubscription = true;
        if (this.state.subscriptionActive) this.toggleSubscription();
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    if (this.stickyButton) {
      // Use capture phase + stopImmediatePropagation so this runs BEFORE the
      // generic w-product-template.js sticky handler (which would otherwise
      // delegate the click and bypass our gift-dedup logic in handleSubmit).
      this.stickyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.handleSubmit(e);
      }, { capture: true });
    }

    if (this.stickyBar && this.ctaButton) {
      window.addEventListener('scroll', () => this.updateStickyVisibility());
      this.updateStickyVisibility();
      // Intelligems body class may arrive after init (on ig:ready), so re-evaluate
      // the sticky rule then without waiting for a scroll.
      window.addEventListener('ig:ready', () => this.updateStickyVisibility());
    }
  }

  selectPerson(person) {
    this.personButtons.forEach(btn => {
      const btnPerson = parseInt(btn.dataset.person || '1', 10);
      btn.classList.toggle('form-subscription__person--active', btnPerson === person);
    });
    const quantityIndex = person === 1 ? 0 : 1;
    this.selectQuantity(quantityIndex);
  }

  selectFlavor(flavor) {
    this.state.selectedFlavor = flavor;
    this.updateUI();
  }

  selectQuantity(index) {
    this.state.selectedQuantityIndex = index;
    this.updateUI();
  }

  toggleSubscription() {
    this.state.subscriptionActive = !this.state.subscriptionActive;
    this.container.dataset.subscriptionActive = this.state.subscriptionActive.toString();
    this.updateUI();
  }

  findVariant(flavor, quantityIndex) {
    const quantities = this.options.quantities || [];
    const targetQuantity = quantities[quantityIndex];
    if (!targetQuantity) return null;
    if (!this.hasSubvariants) {
      if (this.usePickerFlavorDimension) {
        return this.variants.find(v => v.picker_flavor === flavor && v.option1 === targetQuantity) || null;
      }
      return this.variants.find(v => v.option1 === targetQuantity) || null;
    }
    return this.variants.find(v => v.option1 === flavor && v.option2 === targetQuantity) || null;
  }

  getCurrentVariant() {
    const v = this.findVariant(this.state.selectedFlavor, this.state.selectedQuantityIndex);
    if (v) return v;
    if (!this.hasSubvariants) {
      if (this.usePickerFlavorDimension) {
        return this.variants.find(vr => vr.picker_flavor === this.state.selectedFlavor) || this.variants[0] || null;
      }
      return this.variants[0] || null;
    }
    return this.variants.find(v => v.option1 === this.state.selectedFlavor) || this.variants[0];
  }

  updateUI() {
    this.updateFlavorUI();
    this.updateVariantUI();
    this.updateToggleUI();
    this.updateDiscountBannerUI();
    this.updateTitle();
    this.updatePrices();
    this.updateGiftsUI();
    this.updateCTAState();
    this.updateFormInputs();
    this.updateSwitcherUI();
  }

  // RA_PT_PDP_1 Var B | New switcher. Shows the subscription first-order discount on
  // the "Abonnement" card and mirrors the active variant's sub-benefits (recurring set
  // with gifts, fallback set without) into the card. The "Achat unique" card mirrors
  // one_time_checkmarks the same way when that setting is filled.
  updateSwitcherUI() {
    if (!this.switcher) return;

    this.switcher.dataset.subscriptionActive = this.state.subscriptionActive.toString();
    if (this.switcherSubOption) {
      this.switcherSubOption.classList.toggle('form-subscription__switcher-option--active', this.state.subscriptionActive);
    }
    if (this.switcherOneTimeOption) {
      this.switcherOneTimeOption.classList.toggle('form-subscription__switcher-option--active', !this.state.subscriptionActive);
    }

    const variant = this.getCurrentVariant();

    // Subscription first-order discount (e.g. -23%), independent of the selected mode.
    let firstOrderDiscountLabel = '';
    if (variant && variant.selling_plan_allocations?.length > 0) {
      const subPrice = this.getSubscriptionDisplayPrice(variant);
      const comparePrice = variant.compare_at_price || variant.price;
      if (comparePrice > subPrice) {
        firstOrderDiscountLabel = `-${Math.round((1 - subPrice / comparePrice) * 100)}%`;
      }
    }

    if (this.switcherDiscount) {
      this.switcherDiscount.textContent = firstOrderDiscountLabel;
      this.switcherDiscount.style.display = firstOrderDiscountLabel ? '' : 'none';
    }

    if (this.switcherOneTimeBenefits) {
      const activeItem = this.container.querySelector('.form-subscription__variant--active');
      const oneTimeBenefits = activeItem?.querySelector('.form-subscription__one-time-benefits');
      this.switcherOneTimeBenefits.innerHTML = oneTimeBenefits ? oneTimeBenefits.innerHTML : '';
    }

    if (this.switcherBenefits) {
      const activeItem = this.container.querySelector('.form-subscription__variant--active');
      const benefits = activeItem?.querySelector('.form-subscription__sub-benefits');
      this.switcherBenefits.innerHTML = benefits ? benefits.innerHTML : '';

      // The cloned spans reflect the currently selected mode; the "Abonnement" card must
      // always describe the subscription, so override them with subscription-specific values.
      const rechargeDiscountLabel = this.getRechargeSubscriptionDiscountLabel(variant);
      this.switcherBenefits.querySelectorAll('.form-subscription__first-discount-value').forEach((el) => {
        el.textContent = firstOrderDiscountLabel;
      });
      this.switcherBenefits.querySelectorAll('.form-subscription__subscription-discount-value').forEach((el) => {
        el.textContent = rechargeDiscountLabel;
      });
    }
  }

  updateCTAState() {
    const variant = this.getCurrentVariant();
    const isAvailable = !!variant?.available;

    if (this.ctaButton) {
      this.ctaButton.disabled = !isAvailable;
      this.ctaButton.classList.toggle('form-subscription__cta--unavailable', !isAvailable);
    }
    if (this.ctaLabelAvailable) {
      this.ctaLabelAvailable.style.display = isAvailable ? '' : 'none';
    }
    if (this.ctaLabelUnavailable) {
      this.ctaLabelUnavailable.style.display = isAvailable ? 'none' : '';
    }
    if (this.ctaDot) {
      this.ctaDot.style.display = isAvailable ? '' : 'none';
    }
    if (this.ctaPrice) {
      this.ctaPrice.style.display = isAvailable ? '' : 'none';
    }
    if (this.ctaCompare && !isAvailable) {
      this.ctaCompare.style.display = 'none';
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = !isAvailable;
    }
  }

  updateTitle() {
    if (!this.titleHeading) return;
    const variant = this.getCurrentVariant();
    const variantTitle = typeof variant?.custom_product_title === 'string'
      ? variant.custom_product_title.trim()
      : '';
    this.titleHeading.textContent = variantTitle || this.defaultProductTitle;
  }

  updateFlavorUI() {
    this.flavorButtons.forEach(btn => {
      const isActive = btn.dataset.flavor === this.state.selectedFlavor;
      btn.classList.toggle('form-subscription__flavor-btn--active', isActive);
    });
  }

  updateVariantUI() {
    let firstVisibleIndex = null;

    this.variantItems.forEach((item, index) => {
      const variant = this.findVariant(this.state.selectedFlavor, index);
      if (variant === null) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
        if (firstVisibleIndex === null) firstVisibleIndex = index;
      }
    });

    if (firstVisibleIndex !== null) {
      const activeItem = this.variantItems[this.state.selectedQuantityIndex];
      const currentVisible = activeItem && activeItem.style.display !== 'none';
      if (!currentVisible) {
        this.state.selectedQuantityIndex = firstVisibleIndex;
      }
    }

    const currentVariant = this.getCurrentVariant();
    this.variantItems.forEach((item, index) => {
      const variant = this.findVariant(this.state.selectedFlavor, index);
      const isActive = variant && currentVariant && variant.id === currentVariant.id;
      item.classList.toggle('form-subscription__variant--active', !!isActive);

      const isOos = !!variant && !variant.available;
      item.classList.toggle('form-subscription__variant--out-of-stock', isOos);
      item.dataset.outOfStock = String(isOos);
      if (isOos) {
        item.setAttribute('aria-disabled', 'true');
      } else {
        item.removeAttribute('aria-disabled');
      }

      if (variant && this.hasSubvariants) {
        const [bold, ...light] = (variant.quantity_label || variant.option2 || '').split(':');
        const boldEl = item.querySelector('.form-subscription__variant-title-bold');
        const lightEl = item.querySelector('.form-subscription__variant-title-light');
        if (boldEl) boldEl.textContent = light.length ? `${bold.trim()} :` : bold.trim();
        if (lightEl) lightEl.textContent = light.join(':').trim();
      }

      const soldOutBadge = item.querySelector('.form-subscription__badge-sold-out');
      if (soldOutBadge) {
        soldOutBadge.style.display = isOos ? '' : 'none';
      }
      const discountBadge = item.querySelector('.form-subscription__badge-discount');
      if (discountBadge && isOos) {
        discountBadge.style.display = 'none';
      }
    });
  }

  updateToggleUI() {
    if (this.toggle) {
      this.toggle.classList.toggle('form-subscription__toggle--off', !this.state.subscriptionActive);
    }
  }

  updateDiscountBannerUI() {
    if (!this.discountBanner) return;

    const subscriptionText = this.discountBanner.querySelector(
      '.form-subscription__discount-banner-text--subscription'
    );
    const oneTimeText = this.discountBanner.querySelector(
      '.form-subscription__discount-banner-text--one-time'
    );
    const showBanner = this.state.subscriptionActive
      ? !!subscriptionText
      : !!oneTimeText;

    this.discountBanner.style.display = showBanner ? '' : 'none';
  }

  updateStickyVisibility() {
    if (!this.stickyBar || !this.ctaButton) return;
    const ctaRect = this.ctaButton.getBoundingClientRect();
    // RA_PT_PDP_1 shipped to production: the sticky bar only appears after the user has
    // scrolled past the CTA (previously gated behind c-subscriptionImprovements-varA/varB).
    const shouldShow = ctaRect.bottom < 0;
    this.stickyBar.classList.toggle('active', shouldShow);
  }

  updatePrices() {
    const variant = this.getCurrentVariant();
    if (!variant) return;

    let displayPrice = variant.price;
    let comparePrice = variant.compare_at_price || variant.price;

    if (this.state.subscriptionActive && variant.selling_plan_allocations?.length > 0) {
      // First-order discount = on one-time price. Automatic discount = on subscription price ("on top").
      displayPrice = this.getSubscriptionDisplayPrice(variant);
      comparePrice = variant.compare_at_price || variant.price;
    } else {
      displayPrice = this.getEffectiveOneshotPrice(variant.price);
    }

    if (this.ctaPrice) {
      this.ctaPrice.textContent = this.formatMoney(displayPrice);
    }
    if (this.ctaCompare) {
      if (comparePrice > displayPrice) {
        this.ctaCompare.textContent = this.formatMoney(comparePrice);
        this.ctaCompare.style.display = 'inline';
      } else {
        this.ctaCompare.style.display = 'none';
      }
    }

    if (this.stickyPriceEl) {
      this.stickyPriceEl.textContent = this.formatMoney(displayPrice);
    }
    if (this.stickyCompareEl) {
      if (comparePrice > displayPrice) {
        this.stickyCompareEl.textContent = this.formatMoney(comparePrice);
        this.stickyCompareEl.style.display = 'inline';
      } else {
        this.stickyCompareEl.style.display = 'none';
      }
    }

    this.titlePriceEls.forEach(el => {
      el.textContent = this.formatMoney(displayPrice);
    });
    this.titleCompareEls.forEach(el => {
      if (comparePrice > displayPrice) {
        el.textContent = this.formatMoney(comparePrice);
        el.style.display = 'inline';
      } else {
        el.style.display = 'none';
      }
    });
    if (this.dividedBy > 0) {
      const dividedPrice = Math.round(displayPrice / this.dividedBy);
      this.titleDividedEls.forEach(el => {
        el.textContent = this.formatMoney(dividedPrice);
      });
    }

    this.updateVariantPrices();
  }

  updateVariantPrices() {
    const quantities = this.options.quantities || [];

    this.variantItems.forEach((item, index) => {
      const quantity = quantities[index];
      if (!quantity) return;

      const variant = this.findVariant(this.state.selectedFlavor, index);
      if (!variant) return;

      const doses = parseInt(item.dataset.doses || '0', 10);
      const unitSuffix = item.dataset.unitSuffix || '/tasse';

      let subDisplayPrice = variant.price;
      let subComparePrice = variant.compare_at_price || variant.price;
      if (variant.selling_plan_allocations?.length > 0) {
        // First-order discount = on one-time price. Automatic discount = on subscription price ("on top").
        subDisplayPrice = this.getSubscriptionDisplayPrice(variant);
        subComparePrice = variant.compare_at_price || variant.price;
      }

      const oneshotPrice = this.getEffectiveOneshotPrice(variant.price);
      const oneshotCompare = variant.compare_at_price || variant.price;

      const displayPrice = this.state.subscriptionActive ? subDisplayPrice : oneshotPrice;
      const comparePrice = this.state.subscriptionActive ? subComparePrice : oneshotCompare;

      const subPriceEl = item.querySelector('.form-subscription__variant-pricing .form-subscription__price');
      const subCompareEl = item.querySelector('.form-subscription__price-compare');
      const subUnitEl = item.querySelector('.form-subscription__price-unit');

      if (subPriceEl) subPriceEl.textContent = this.formatMoney(subDisplayPrice);
      if (subCompareEl) {
        subCompareEl.textContent = this.formatMoney(subComparePrice);
        subCompareEl.style.display = subComparePrice > subDisplayPrice ? 'inline' : 'none';
      }
      if (subUnitEl && doses > 0) {
        const subUnitPrice = subDisplayPrice / doses;
        subUnitEl.textContent = this.formatMoney(subUnitPrice) + unitSuffix;
      }

      const oneshotPriceEl = item.querySelector('.form-subscription__variant-price-row .form-subscription__price');
      const oneshotCompareEl = item.querySelector('.form-subscription__price-compare-inline');
      const oneshotUnitEl = item.querySelector('.form-subscription__price-unit-inline');

      if (oneshotPriceEl) oneshotPriceEl.textContent = this.formatMoney(oneshotPrice);
      if (oneshotCompareEl) {
        oneshotCompareEl.textContent = this.formatMoney(oneshotCompare);
        oneshotCompareEl.style.display = oneshotCompare > oneshotPrice ? 'inline' : 'none';
      }
      if (oneshotUnitEl && doses > 0) {
        const oneshotUnitPrice = oneshotPrice / doses;
        oneshotUnitEl.textContent = this.formatMoney(oneshotUnitPrice) + unitSuffix;
      }

      const discountBadge = item.querySelector('.form-subscription__badge-discount');
      let firstSubscriptionDiscountLabel = '';
      if (discountBadge) {
        if (comparePrice > displayPrice) {
          const discountPercent = Math.round((1 - displayPrice / comparePrice) * 100);
          discountBadge.textContent = `-${discountPercent}%`;
          discountBadge.style.display = variant.available ? 'block' : 'none';
          firstSubscriptionDiscountLabel = discountBadge.textContent.trim();
        } else {
          discountBadge.style.display = 'none';
          firstSubscriptionDiscountLabel = '';
        }
      }

      item.querySelectorAll('.form-subscription__first-discount-value').forEach((el) => {
        el.textContent = firstSubscriptionDiscountLabel;
      });

      item.querySelectorAll('.form-subscription__subscription-discount-value').forEach((el) => {
        el.textContent = this.getRechargeSubscriptionDiscountLabel(variant);
      });
    });
  }

  getRechargeSubscriptionDiscountLabel(variant) {
    if (!variant?.selling_plan_allocations?.length) return '';
    const rechargeSubscriptionPrice = variant.selling_plan_allocations[0].price;
    const rechargeComparePrice = variant.compare_at_price || variant.price;
    if (!rechargeComparePrice || rechargeSubscriptionPrice >= rechargeComparePrice) return '';
    const discountPercent = Math.round((1 - rechargeSubscriptionPrice / rechargeComparePrice) * 100);
    return `-${discountPercent}%`;
  }

  updateGiftsUI() {
    const quantities = this.options.quantities || [];

    this.variantItems.forEach((item, index) => {
      const quantity = quantities[index];
      if (!quantity) return;

      const variant = this.findVariant(this.state.selectedFlavor, index);
      if (!variant) return;

      const gifts = variant.gifts || [];
      const giftsTotal = gifts.reduce((sum, g) => sum + (g.price || 0), 0);

      // The preview thumbnails and the [gifts_value] spans are rendered from the control
      // bundle of the initially selected variant, so they have to follow both the flavour
      // change and the RA_PT_PDP_4 gift set swap.
      const preview = item.querySelector('.form-subscription__variant-gifts-preview');
      if (preview) {
        preview.style.display = gifts.length === 0 ? 'none' : '';
        const previewImgs = preview.querySelector('.form-subscription__variant-gifts-preview-imgs');
        if (previewImgs) {
          previewImgs.innerHTML = gifts
            .filter(gift => gift.image)
            .map(gift => `<img src="${gift.image}" alt="" class="form-subscription__variant-gifts-preview-img" width="24" height="24" loading="lazy">`)
            .join('');
        }
      }

      item.querySelectorAll('.form-subscription__gifts-value').forEach((el) => {
        el.textContent = this.formatMoney(giftsTotal);
      });

      const giftsContainer = item.querySelector('.form-subscription__gifts');

      if (giftsContainer) {
        giftsContainer.dataset.hasGifts = gifts.length > 0 ? 'true' : 'false';
        if (!variant.available) {
          giftsContainer.style.display = 'none';
          return;
        }

        const giftsBanner = giftsContainer.querySelector('.form-subscription__gifts-banner');
        const giftsGrid = giftsContainer.querySelector('.form-subscription__gifts-grid');
        const subBenefits = giftsContainer.querySelector('.form-subscription__sub-benefits');

        if (gifts.length === 0) {
          if (giftsBanner) giftsBanner.style.display = 'none';
          if (giftsGrid) giftsGrid.style.display = 'none';
          const hasFallback = subBenefits && subBenefits.children.length > 0;
          giftsContainer.style.display = hasFallback ? '' : 'none';
        } else {
          giftsContainer.style.display = '';
          if (giftsBanner) giftsBanner.style.display = '';
          if (giftsGrid) giftsGrid.style.display = '';

          const bannerPrice = giftsContainer.querySelector('.form-subscription__gifts-banner-price');
          if (bannerPrice) bannerPrice.textContent = this.formatMoney(giftsTotal);

          if (giftsGrid) {
            giftsGrid.innerHTML = gifts.map(gift => `
              <div class="form-subscription__gift">
                <div class="form-subscription__gift-tag">
                  <span class="form-subscription__gift-tag-price">${this.formatMoney(gift.price || 0)}</span>
                  <span class="form-subscription__gift-tag-text">OFFERT</span>
                </div>
                <div class="form-subscription__gift-box">
                  ${gift.image ? `<img src="${gift.image}" alt="${gift.title}" class="form-subscription__gift-img" loading="lazy">` : ''}
                </div>
                <span class="form-subscription__gift-name">${gift.title}</span>
              </div>
            `).join('');
          }
        }
      }
    });
  }

  updateFormInputs() {
    const variant = this.getCurrentVariant();
    if (!variant) return;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.state.subscriptionActive) {
      const subInput = this.container.querySelector('input[name="selling_plan"].first');
      if (subInput) subInput.checked = true;
    } else {
      const oneshotInput = this.container.querySelector('input[name="selling_plan"].no-abo-ok');
      if (oneshotInput) oneshotInput.checked = true;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    
    // Trigger variant picker to update images
    const variantPicker = document.querySelector('variant-picker') ||
                         this.container.closest('.js-product')?.querySelector('variant-picker') ||
                         this.container.closest('product-template')?.querySelector('variant-picker') ||
                         document.querySelector('.js-product')?.querySelector('variant-picker') ||
                         document.querySelector('product-template')?.querySelector('variant-picker');
    
    if (variantPicker) {
      variantPicker.variant = variant;
      if (document.querySelector('product-template')) {
        variantPicker.product_tempalte = true;
      }
      variantPicker.updatePrice();
      variantPicker.dispatchEvent(
        new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: variantPicker.productForm || this.container,
            variant,
            product: variantPicker.data?.product,
          },
        })
      );
    } else {
      // Fallback: update images directly if variant-picker doesn't exist
      this.updateImagesDirectly(variant);
      this.container.dispatchEvent(
        new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.container,
            variant,
          },
        })
      );
    }
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `€${euros}`;
  }

  /**
   * Fallback method to update images directly if variant-picker is not available
   */
  updateImagesDirectly(variant) {
    const hasChangableImages = !!document.querySelector("[data-changable-image]");
    if (!hasChangableImages || !variant) return;
    
    // Extract parts from variant title (matching Liquid template logic)
    const variantTitle = variant.title;
    const titleParts = variantTitle.split('/');
    const variantFirstPart = titleParts[0]?.trim() || '';
    const variantLastPart = titleParts[titleParts.length - 1]?.trim() || '';

    // Helper function to check if image should be shown (matching Liquid logic)
    const shouldShowImage = (imageAlt) => {
      if (!imageAlt || !imageAlt.includes('/')) {
        return true; // Generic image
      }

      const imageAltParts = imageAlt.split('/');
      const imageFirstPart = imageAltParts[0]?.trim() || '';
      const imageLastPart = imageAltParts[imageAltParts.length - 1]?.trim() || '';

      if (imageFirstPart === '') {
        return imageLastPart === variantLastPart;
      } else if (imageLastPart === '') {
        return imageFirstPart === variantFirstPart;
      } else {
        return imageFirstPart === variantFirstPart && imageLastPart === variantLastPart;
      }
    };

    let firstFullWidthSet = false;
    let firstFullWidthThumbSet = false;

    // Update main images
    document.querySelectorAll("[data-changable-image]").forEach((elem) => {
      const imageAlt = elem.getAttribute('data-image-alt') || '';
      
      if (shouldShowImage(imageAlt)) {
        elem.classList.remove("hidden");
        elem.classList.add("swiper-slide");
        if (!firstFullWidthSet) {
          elem.classList.add("full-width");
          firstFullWidthSet = true;
        }
      } else {
        elem.classList.add("hidden");
        elem.classList.remove("full-width");
        elem.classList.remove("swiper-slide");
      }
    });

    // Update thumbnail images
    document.querySelectorAll("[data-changable-image-thumb]").forEach((elem) => {
      const imageAlt = elem.getAttribute('data-image-alt') || '';
      
      if (shouldShowImage(imageAlt)) {
        elem.classList.remove("hidden");
        elem.classList.add("swiper-slide");
        if (!firstFullWidthThumbSet) {
          elem.classList.add("full-width-thumb");
          firstFullWidthThumbSet = true;
        }
        elem.classList.add("show");
      } else {
        elem.classList.add("hidden");
        elem.classList.remove("full-width-thumb");
        elem.classList.remove("swiper-slide");
        elem.classList.remove("show");
      }
    });

    // Update swiper if on mobile
    if (window.innerWidth < 769) {
      const productMedia = document.querySelector("product-media");
      if (productMedia && productMedia.updateSwiper) {
        productMedia.updateSwiper();
      }
    }
  }

  /**
   * Subscription first-order display price: section first-order discount only, always on one-time purchase price.
   * When section has no first-order discount, use getSubscriptionDisplayPrice instead (which may apply automatic to subscription price).
   */
  getSubscriptionFirstOrderDisplayPrice(oneTimePriceCents) {
    let price = oneTimePriceCents;
    const sectionAmount = Number(this.firstOrderDiscountAmount) || 0;
    const sectionPercent = Number(this.firstOrderDiscountPercent) || 0;
    if (sectionAmount > 0) {
      price = price - sectionAmount;
    } else if (sectionPercent > 0) {
      price = price * (1 - sectionPercent / 100);
    }
    return Math.max(0, Math.round(price));
  }

  /**
   * Apply automatic discount (percentage or amount) to a price in cents. Used for subscription price when automatic is "on top".
   */
  applyAutomaticDiscount(priceCents) {
    let price = priceCents;
    const amount = Number(this.automaticDiscountAmount) || 0;
    const percent = Number(this.automaticDiscountPercent) || 0;
    if (amount > 0) {
      price = price - amount;
    } else if (percent > 0) {
      price = price * (1 - percent / 100);
    }
    return Math.max(0, Math.round(price));
  }

  /**
   * Subscription display price: first-order discount on one-time price when set; otherwise automatic discount on subscription price when enabled; else raw subscription price.
   */
  getSubscriptionDisplayPrice(variant) {
    if (!variant.selling_plan_allocations?.length) return variant.price;
    const sectionAmount = Number(this.firstOrderDiscountAmount) || 0;
    const sectionPercent = Number(this.firstOrderDiscountPercent) || 0;
    const hasSectionDiscount = sectionAmount > 0 || sectionPercent > 0;

    if (hasSectionDiscount) {
      return this.getSubscriptionFirstOrderDisplayPrice(variant.price);
    }
    const subPrice = variant.custom_subscription_price > 0
      ? variant.custom_subscription_price
      : variant.selling_plan_allocations[0].price;
    if (this.enableAutomaticDiscount) {
      return this.applyAutomaticDiscount(subPrice);
    }
    return subPrice;
  }

  calculateFirstOrderPrice(basePrice) {
    let price = basePrice;
    const useAutomatic = this.enableAutomaticDiscount;
    const amount = useAutomatic ? this.automaticDiscountAmount : this.firstOrderDiscountAmount;
    const percent = useAutomatic ? this.automaticDiscountPercent : this.firstOrderDiscountPercent;
    if (amount > 0) {
      price = price - amount;
    } else if (percent > 0) {
      price = price * (1 - percent / 100);
    }
    return Math.max(0, Math.round(price));
  }

  /**
   * One-shot price with manual discount applied only when manual discount is enabled and not subscription-only.
   * Uses its own percentage: the subscription one stacks on top of the selling plan discount,
   * so the same total off the regular price needs a different number here.
   */
  getEffectiveOneshotPrice(basePrice) {
    if (!this.enableAutomaticDiscount || this.automaticDiscountSubscriptionOnly) {
      return basePrice;
    }
    if (this.automaticDiscountAmount > 0) {
      return Math.max(0, Math.round(basePrice - this.automaticDiscountAmount));
    }
    if (this.automaticDiscountPercentOneTime > 0) {
      return Math.max(0, Math.round(basePrice * (1 - this.automaticDiscountPercentOneTime / 100)));
    }
    return basePrice;
  }

  async handleSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    const variant = this.getCurrentVariant();
    if (!variant) return;

    if (this.ctaButton) {
      this.ctaButton.disabled = true;
      this.ctaButton.classList.add('form-subscription__cta--loading');
    }

    const items = [];
    const mainItem = { id: variant.id, quantity: 1 };

    const withSellingPlan = this.state.subscriptionActive && variant.selling_plan_allocations?.length > 0;
    if (withSellingPlan) {
      mainItem.selling_plan = variant.selling_plan_allocations[0].selling_plan_id;
    }

    if (withSellingPlan || this.giftsOneTimePurchase) {
      const variantGifts = variant.gifts || [];
      if (variantGifts.length > 0) {
        let giftsToAdd = variantGifts;

        if (!this.cumulateGifts) {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          const cartItemIds = cartData.items.map(item => item.variant_id);
          giftsToAdd = variantGifts.filter(gift => !cartItemIds.includes(gift.id));
        }

        giftsToAdd.forEach(gift => {
          items.push({
            id: gift.id,
            quantity: 1,
            properties: {
              '_gift': 'true',
              '_parent_variant': variant.id.toString()
            }
          });
        });
      }
    }

    items.push(mainItem);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        if (this.directCheckout) {
          window.location.href = '/checkout';
          return;
        }

        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        const sideCart = document.querySelector('side-cart');
        if (sideCart) {
          sideCart.dispatchEvent(
            new CustomEvent('dispatch:cart-drawer:refresh', {
              detail: {
                cart: cartData,
                new_item_added: true,
                open: true
              }
            })
          );
        }
      } else {
        console.error('Add to cart error:', await response.json());
      }
    } catch (err) {
      console.error('Add to cart error:', err);
    }

    if (this.ctaButton) {
      this.ctaButton.disabled = false;
      this.ctaButton.classList.remove('form-subscription__cta--loading');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.form-subscription').forEach(container => {
    new FormSubscription(container);
  });
});

document.addEventListener('shopify:section:load', (e) => {
  const container = e.target.querySelector('.form-subscription');
  if (container) new FormSubscription(container);
});
